const OPEN_CLASS = "is-open"
const VIEWPORT_MARGIN = 16
const GAP = 8
const TOOLTIP = "[data-tooltip]"
const BUTTON = "[data-tooltip-button]"
const TEXT = "[data-tooltip-text]"

let openTooltip: HTMLElement | null = null
let installed = false

// Tooltips of the credits /plans comparison table. Every handler is delegated from the document,
// so the count of tooltips on the page does not matter. The bubble is fixed to the viewport, above
// the icon when it fits and below it otherwise, and follows the icon when the page scrolls. A tap
// toggles it; on devices with hover the pointer opens and closes it and a click keeps it open;
// keyboard focus opens it; a click outside or Escape closes it.
export default function setupCreditsPlansTooltips() {
  if (installed) return
  installed = true

  document.addEventListener("click", event => {
    const button = buttonOf(event.target)
    if (!button) {
      if (openTooltip && !openTooltip.contains(event.target as Node)) close()
      return
    }
    const tooltip = tooltipOf(button)
    if (openTooltip === tooltip && !hoverDevice()) {
      close()
    } else {
      open(tooltip)
    }
  })

  document.addEventListener("mouseover", event => {
    const button = buttonOf(event.target)
    if (button && hoverDevice()) open(tooltipOf(button))
  })

  document.addEventListener("mouseout", event => {
    if (!openTooltip || !hoverDevice()) return
    const leavingTooltip = openTooltip.contains(event.target as Node)
    const stillInside = openTooltip.contains(event.relatedTarget as Node)
    const focused = openTooltip.querySelector(BUTTON)?.matches(":focus-visible")
    if (leavingTooltip && !stillInside && !focused) close()
  })

  document.addEventListener("focusin", event => {
    const button = buttonOf(event.target)
    if (button && button.matches(":focus-visible")) open(tooltipOf(button))
  })

  document.addEventListener("focusout", event => {
    const button = buttonOf(event.target)
    if (button && openTooltip === tooltipOf(button)) close()
  })

  document.addEventListener("keydown", event => {
    if (event.key === "Escape") close()
  })
  document.addEventListener("scroll", follow, true)
  window.addEventListener("resize", follow)
}

function buttonOf(target: EventTarget | null) {
  return target instanceof Element ? target.closest<HTMLButtonElement>(BUTTON) : null
}

function tooltipOf(button: HTMLButtonElement) {
  return button.closest<HTMLElement>(TOOLTIP)!
}

function hoverDevice() {
  return window.matchMedia("(hover: hover)").matches
}

function open(tooltip: HTMLElement) {
  if (openTooltip === tooltip) return
  close()
  const button = tooltip.querySelector<HTMLButtonElement>(BUTTON)
  const text = tooltip.querySelector<HTMLElement>(TEXT)
  if (!button || !text) return

  tooltip.classList.add(OPEN_CLASS)
  button.setAttribute("aria-expanded", "true")
  openTooltip = tooltip
  place(button, text)
}

function follow() {
  if (!openTooltip) return
  const button = openTooltip.querySelector<HTMLButtonElement>(BUTTON)
  const text = openTooltip.querySelector<HTMLElement>(TEXT)
  if (button && text) place(button, text)
}

function close() {
  if (!openTooltip) return
  openTooltip.classList.remove(OPEN_CLASS)
  openTooltip.querySelector(BUTTON)?.setAttribute("aria-expanded", "false")
  openTooltip = null
}

function place(button: HTMLButtonElement, text: HTMLElement) {
  const anchor = button.getBoundingClientRect()
  const width = text.offsetWidth
  const height = text.offsetHeight
  const maxLeft = window.innerWidth - VIEWPORT_MARGIN - width
  const left = Math.max(VIEWPORT_MARGIN, Math.min(anchor.left, maxLeft))
  const above = anchor.top - GAP - height
  const top = above >= VIEWPORT_MARGIN ? above : anchor.bottom + GAP

  text.style.left = `${Math.round(left)}px`
  text.style.top = `${Math.round(top)}px`
}
